import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Seo } from '../components/Seo';
import { seoPages } from '../seo';

const PrivacyPolicy = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#000000] pt-32 pb-24">
      <Seo title={seoPages.privacy.title} description={seoPages.privacy.description} />
      <div className="container mx-auto px-5 md:px-10 max-w-4xl">
        <h1 className="heading-2 text-[white] mb-8">
          {t({ 
            sl: 'POLITIKA ZASEBNOSTI', 
            en: 'PRIVACY POLICY', 
            it: 'INFORMATIVA SULLA PRIVACY' 
          })}
        </h1>

        <div className="prose prose-invert max-w-none space-y-8 text-[#b0b0b0]">
          
          {/* Section 1 */}
          <section>
            <h2 className="text-white text-xl font-bold mb-4">
              {t({ 
                sl: '1. Upravljavec osebnih podatkov', 
                en: '1. Data Controller', 
                it: '1. Titolare del Trattamento' 
              })}
            </h2>
            <p className="body-medium leading-relaxed">
              {t({
                sl: 'Upravljavec osebnih podatkov je Matrix Print, Ulica 15. maja 10, 6000 Koper, Slovenija. Za vsa vprašanja glede varstva osebnih podatkov nas lahko kontaktirate na info@matrixprint.si.',
                en: 'The data controller is Matrix Print, Ulica 15. maja 10, 6000 Koper, Slovenia. For all questions regarding personal data protection, you can contact us at info@matrixprint.si.',
                it: 'Il titolare del trattamento è Matrix Print, Ulica 15. maja 10, 6000 Capodistria, Slovenia. Per tutte le domande sulla protezione dei dati personali, potete contattarci a info@matrixprint.si.'
              })}
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-white text-xl font-bold mb-4">
              {t({ 
                sl: '2. Katere podatke zbiramo', 
                en: '2. What Data We Collect', 
                it: '2. Quali Dati Raccogliamo' 
              })}
            </h2>
            <p className="body-medium leading-relaxed mb-4">
              {t({
                sl: 'Preko kontaktnega obrazca zbiramo naslednje osebne podatke:',
                en: 'Through the contact form, we collect the following personal data:',
                it: 'Attraverso il modulo di contatto, raccogliamo i seguenti dati personali:'
              })}
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>{t({ sl: 'Ime in priimek', en: 'Full name', it: 'Nome e cognome' })}</li>
              <li>{t({ sl: 'E-poštni naslov', en: 'Email address', it: 'Indirizzo email' })}</li>
              <li>{t({ sl: 'Telefonska številka (opcijsko)', en: 'Phone number (optional)', it: 'Numero di telefono (opzionale)' })}</li>
              <li>{t({ sl: 'Vsebina sporočila', en: 'Message content', it: 'Contenuto del messaggio' })}</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-white text-xl font-bold mb-4">
              {t({ 
                sl: '3. Namen obdelave podatkov', 
                en: '3. Purpose of Data Processing', 
                it: '3. Finalità del Trattamento' 
              })}
            </h2>
            <p className="body-medium leading-relaxed">
              {t({
                sl: 'Vaše osebne podatke uporabljamo izključno za odgovor na vaše povpraševanje in komunikacijo v zvezi z našimi storitvami. Podatkov ne uporabljamo za trženje ali jih delimo s tretjimi osebami.',
                en: 'We use your personal data exclusively to respond to your inquiry and communicate regarding our services. We do not use the data for marketing or share it with third parties.',
                it: 'Utilizziamo i vostri dati personali esclusivamente per rispondere alla vostra richiesta e comunicare riguardo ai nostri servizi. Non utilizziamo i dati per marketing né li condividiamo con terze parti.'
              })}
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-white text-xl font-bold mb-4">
              {t({ 
                sl: '4. Pravna podlaga', 
                en: '4. Legal Basis', 
                it: '4. Base Giuridica' 
              })}
            </h2>
            <p className="body-medium leading-relaxed">
              {t({
                sl: 'Obdelava vaših osebnih podatkov temelji na vašem soglasju (člen 6(1)(a) GDPR), ki ga podate z označitvijo polja za strinjanje v kontaktnem obrazcu.',
                en: 'The processing of your personal data is based on your consent (Article 6(1)(a) GDPR), which you provide by checking the consent box in the contact form.',
                it: 'Il trattamento dei vostri dati personali si basa sul vostro consenso (Articolo 6(1)(a) GDPR), che fornite selezionando la casella di consenso nel modulo di contatto.'
              })}
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-white text-xl font-bold mb-4">
              {t({ 
                sl: '5. Hramba podatkov', 
                en: '5. Data Retention', 
                it: '5. Conservazione dei Dati' 
              })}
            </h2>
            <p className="body-medium leading-relaxed">
              {t({
                sl: 'Vaše osebne podatke hranimo le toliko časa, kolikor je potrebno za obravnavo vašega povpraševanja, vendar ne dlje kot 2 leti od zadnjega kontakta.',
                en: 'We retain your personal data only for as long as necessary to handle your inquiry, but no longer than 2 years from the last contact.',
                it: 'Conserviamo i vostri dati personali solo per il tempo necessario a gestire la vostra richiesta, ma non oltre 2 anni dall\'ultimo contatto.'
              })}
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-white text-xl font-bold mb-4">
              {t({ 
                sl: '6. Vaše pravice', 
                en: '6. Your Rights', 
                it: '6. I Vostri Diritti' 
              })}
            </h2>
            <p className="body-medium leading-relaxed mb-4">
              {t({
                sl: 'V skladu z GDPR imate naslednje pravice:',
                en: 'Under GDPR, you have the following rights:',
                it: 'Ai sensi del GDPR, avete i seguenti diritti:'
              })}
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>{t({ sl: 'Pravica dostopa do vaših podatkov', en: 'Right to access your data', it: 'Diritto di accesso ai vostri dati' })}</li>
              <li>{t({ sl: 'Pravica do popravka netočnih podatkov', en: 'Right to rectify inaccurate data', it: 'Diritto di rettifica dei dati inesatti' })}</li>
              <li>{t({ sl: 'Pravica do izbrisa podatkov', en: 'Right to erasure of data', it: 'Diritto alla cancellazione dei dati' })}</li>
              <li>{t({ sl: 'Pravica do preklica soglasja', en: 'Right to withdraw consent', it: 'Diritto di revoca del consenso' })}</li>
              <li>{t({ sl: 'Pravica do pritožbe pri nadzornem organu', en: 'Right to lodge a complaint with a supervisory authority', it: 'Diritto di presentare reclamo a un\'autorità di controllo' })}</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-white text-xl font-bold mb-4">
              {t({ 
                sl: '7. Kontakt', 
                en: '7. Contact', 
                it: '7. Contatto' 
              })}
            </h2>
            <p className="body-medium leading-relaxed">
              {t({
                sl: 'Za uveljavljanje svojih pravic ali vprašanja glede varstva osebnih podatkov nas kontaktirajte na:',
                en: 'To exercise your rights or for questions regarding personal data protection, contact us at:',
                it: 'Per esercitare i vostri diritti o per domande sulla protezione dei dati personali, contattateci a:'
              })}
            </p>
            <div className="mt-4 p-4 bg-white/5 rounded-lg">
              <p className="text-white font-semibold">Matrix Print</p>
              <p>Ulica 15. maja 10, 6000 Koper, Slovenija</p>
              <p>E-mail: info@matrixprint.si</p>
              <p>{t({ sl: 'Telefon', en: 'Phone', it: 'Telefono' })}: 031 365 657</p>
            </div>
          </section>

          {/* Last updated */}
          <section className="pt-8 border-t border-white/10">
            <p className="text-sm text-gray-500">
              {t({
                sl: 'Zadnja posodobitev: December 2025',
                en: 'Last updated: December 2025',
                it: 'Ultimo aggiornamento: Dicembre 2025'
              })}
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
